import { type TFunction } from 'i18next';

import { toNumber } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import {
    type ReportWidgetConfig,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
} from 'widgets/ComposableReportWidget';
import {
    buildSpendRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

const TABLE_INITIAL_LIMIT = 5;

interface GetProductSuppliersWidgetConfigProps {
    t: TFunction;
}

const buildProductSupplierTooltipRows =
    (t: TFunction): ReportWidgetTooltipRowsBuilder =>
    (args) => {
        if (args.aggregation?.kind === 'other') {
            const entry = args.payload?.[0];
            const aggregatedValue = toNumber(
                entry?.value as number | string | null,
            );

            return [
                ...args.aggregation.headerRows,
                buildSpendRow({
                    id: 'spend',
                    label: t('spendSummary.spend'),
                    amount: aggregatedValue,
                    total: args.total,
                    valueFormatter: args.valueFormatter,
                    valueColor: entry?.color ?? 'metrics.base',
                }),
            ];
        }

        const resolved = getTooltipPointFromPayload(args);
        const item = resolved?.item;

        if (!item) {
            return null;
        }

        const rows: ReportWidgetTooltipRowConfig[] = [
            {
                id: 'header',
                variant: 'header',
                label: item.label,
                boldLabel: true,
                mutedLabel: true,
                marginBottom: 8,
            },
            buildSpendRow({
                id: 'spend',
                label: t('spendSummary.spend'),
                amount: toNumber(item.primaryValue),
                percent: item.secondaryValues?.percentOfTotal,
                total: args.total,
                valueFormatter: args.valueFormatter,
                valueColor: resolved.color ?? 'metrics.base',
            }),
        ];

        return rows;
    };

export const getProductSuppliersWidgetConfig = ({
    t,
}: GetProductSuppliersWidgetConfigProps): ReportWidgetConfig => ({
    widgetId: 'spend-summary-product-suppliers',
    title: t('spendSummary.suppliersSectionTitle'),
    modelLabel: t('spendSummary.modelLabelSuppliers'),
    layout: 'sideBySide',
    visualization: {
        enabled: true,
        type: 'donut',
        options: {
            palette: 'neutral',
            showLegend: false,
            valueFormatter: formatReportCurrency,
            tooltipRowsBuilder: buildProductSupplierTooltipRows(t),
        },
    },
    pagination: {
        mode: 'client',
        pageSize: TABLE_INITIAL_LIMIT,
        revealAllOnLoadMore: true,
    },
    table: {
        enabled: true,
        columns: [
            {
                id: 'supplier',
                label: t('spendSummary.supplier'),
                valueKey: 'label',
                format: 'text',
                align: 'left',
                bold: true,
                minWidth: 160,
                allowWrap: true,
            },
            {
                id: 'supplierSku',
                label: t('spendSummary.supplierSku'),
                valueKey: 'supplierSku',
                format: 'text',
                align: 'left',
            },
            {
                id: 'quantity',
                label: t('Quantity'),
                valueKey: 'quantity',
                format: 'number',
                align: 'right',
            },
            {
                id: 'totalSpend',
                label: t('spendSummary.spend'),
                valueKey: 'totalSpend',
                format: 'currency',
                align: 'right',
            },
            {
                id: 'percentSpend',
                label: t('spendSummary.percentSpend'),
                valueKey: 'percentOfTotal',
                format: 'percent',
                align: 'right',
            },
        ],
        options: {
            emptyMessage: t('spendSummary.productSuppliersEmpty'),
            showColorSwatchInLabelColumn: 'label',
            loadMoreVariant: 'link',
            sortColumnId: 'totalSpend',
        },
    },
});
